import { useCallback, useEffect, useRef } from "react"

import { RemoteCommandFailure } from "../remote/remote-command"
import type { ApplicationActionContext, ApplicationActionRunResult } from "./application-action"
import { useDocumentInstanceLifetime } from "./document-instance"
import type { ApplicationScope } from "./plugin-runtime"

type MaybePromise<T> = T | Promise<T>
type DocumentLifetime = ReturnType<typeof useDocumentInstanceLifetime>

/** @internal The lifetime that aborts the runs of one action owner. */
export interface ActionRuntime {
  readonly ownerSignal: AbortSignal | undefined
  readonly documentLifetime: DocumentLifetime
  readonly controller: AbortController
  tail: Promise<void>
  pendingCount: number
  disposed: boolean
  dispose(): void
}

export interface ActionHandlers {
  readonly onFailure?: (failure: RemoteCommandFailure) => MaybePromise<void>
  readonly onError?: (error: unknown) => MaybePromise<void>
}

/**
 * @internal An action runtime that lives as long as its document instance, so React Activity hiding does not
 * abort it, and that is aborted by the owner signal, the closing document or unmounting outside a document.
 */
export function useActionRuntime(
  ownerSignal: AbortSignal | undefined,
  onOwnerAbort?: (runtime: ActionRuntime) => void
): () => ActionRuntime {
  const documentLifetime = useDocumentInstanceLifetime()
  const runtimeRef = useRef<ActionRuntime | null>(null)
  const onOwnerAbortRef = useRef(onOwnerAbort)
  onOwnerAbortRef.current = onOwnerAbort

  const createRuntime = useCallback((): ActionRuntime => {
    const controller = new AbortController()
    let removeOwnerAbort: () => void = () => undefined
    const runtime: ActionRuntime = {
      ownerSignal,
      documentLifetime,
      controller,
      tail: Promise.resolve(),
      pendingCount: 0,
      disposed: false,
      dispose() {
        if (runtime.disposed) return
        runtime.disposed = true
        removeOwnerAbort()
        if (!controller.signal.aborted) controller.abort()
      },
    }

    if (ownerSignal != null) {
      const abortFromOwner = () => {
        if (runtime.disposed) return
        onOwnerAbortRef.current?.(runtime)
        runtime.dispose()
      }
      if (ownerSignal.aborted) {
        runtime.dispose()
      } else {
        ownerSignal.addEventListener("abort", abortFromOwner, { once: true })
        removeOwnerAbort = () => ownerSignal.removeEventListener("abort", abortFromOwner)
      }
    }

    return runtime
  }, [documentLifetime, ownerSignal])

  const getRuntime = useCallback(() => {
    let runtime = runtimeRef.current
    if (runtime == null || runtime.ownerSignal !== ownerSignal || runtime.documentLifetime !== documentLifetime) {
      if (runtime != null) {
        runtime.documentLifetime?.release(runtime)
        runtime.dispose()
      }
      runtime = createRuntime()
      runtimeRef.current = runtime
      documentLifetime?.retain(runtime, runtime.dispose)
    }
    return runtime
  }, [createRuntime, documentLifetime, ownerSignal])

  useEffect(() => {
    const runtime = getRuntime()
    documentLifetime?.retain(runtime, runtime.dispose)
    return () => {
      if (documentLifetime != null) {
        if (!documentLifetime.releaseFromEffect(runtime)) return
      } else {
        runtime.dispose()
      }
      if (runtimeRef.current === runtime) runtimeRef.current = null
    }
  }, [documentLifetime, getRuntime])

  return getRuntime
}

/**
 * @internal Runs one action body and classifies how it ended. Business failures, technical errors and aborts
 * are returned instead of thrown; technical errors are reported to telemetry.
 */
export async function executeApplicationAction<TResult>(
  scope: ApplicationScope,
  signal: AbortSignal,
  execute: (context: ApplicationActionContext) => MaybePromise<TResult>,
  handlers: ActionHandlers = {}
): Promise<ApplicationActionRunResult<TResult>> {
  if (signal.aborted) return { status: "aborted" }

  const context: ApplicationActionContext = {
    signal,
    invalidate(...dependencies) {
      scope.dataInvalidation.invalidate(...dependencies)
    },
    async command(command, commandOptions) {
      const outcome = await scope.remote.commands.run(command, { ...commandOptions, signal })
      signal.throwIfAborted()
      return outcome
    },
    async execute(command, commandOptions) {
      const result = await scope.remote.commands.execute(command, { ...commandOptions, signal })
      signal.throwIfAborted()
      return result
    },
  }

  try {
    const value = await execute(context)
    signal.throwIfAborted()
    return { status: "succeeded", value }
  } catch (cause) {
    if (cause instanceof RemoteCommandFailure) {
      if (!signal.aborted) await notifySafely(handlers.onFailure, cause, scope, signal)
      return { status: "failed", failure: cause }
    }
    if (signal.aborted) return { status: "aborted" }
    scope.telemetry.reportError(cause)
    await notifySafely(handlers.onError, cause, scope, signal)
    return { status: "error", error: cause }
  }
}

async function notifySafely<T>(
  handler: ((value: T) => MaybePromise<void>) | undefined,
  value: T,
  scope: ApplicationScope,
  signal: AbortSignal
) {
  if (handler == null) return
  try {
    await handler(value)
  } catch (error) {
    if (!signal.aborted) scope.telemetry.reportError(error)
  }
}
